// Turn an engine log entry ({ type, payload }) into a human sentence. Used by the day
// preview (and reusable by any log view). `ctx` resolves ids to names:
//   armyById(id) -> { name } | undefined ; factionById(id) -> { name } ; poolById(id) -> { troop_name, army_name }
const round = (n) => (n == null ? n : Math.round(n).toLocaleString())

export function formatLogEntry(e, ctx = {}) {
  const army = (id) => (ctx.armyById?.(id)?.name) || `army ${id}`
  const faction = (id) => (ctx.factionById?.(id)?.name) || `faction ${id}`
  const pool = (id) => { const p = ctx.poolById?.(id); return p ? `${p.troop_name}${p.army_name ? ` (${p.army_name})` : ''}` : `pool ${id}` }
  const p = e.payload || {}
  switch (e.type) {
    case 'revenue':
      return `💰 ${faction(p.factionId)} collected ${round(p.gp)} gp in taxes`
    case 'wage':
      if (p.action === 'paid') return `🪙 Paid ${round(p.cost)} gp wages — ${pool(p.poolId)}`
      if (p.action === 'unpaid-insufficient') return `⚠ ${faction(p.factionId)} can't cover ${round(p.owed)} gp wages (treasury ${round(p.treasury)})`
      if (p.action === 'restless') return `😠 ${pool(p.poolId)} growing restless (${p.daysUnpaid}d unpaid)`
      if (p.action === 'morale-check') return `🎲 ${pool(p.poolId)} morale check (${p.daysUnpaid}d unpaid): rolled ${p.roll} vs ${p.morale} — ${p.result === 'pass' ? 'holds' : 'DESERTS'}`
      return `wage: ${p.action}`
    case 'supply': {
      const bad = p.status === 'half' || p.status === 'short'
      const tag = bad ? ` — ⚠ ${p.status === 'short' ? 'under-supplied' : 'half rations'}` : ''
      return `🥖 ${army(p.armyId)} supply: need ${round(p.need)} (forage ${round(p.local)} + carried ${round(p.carried)})${tag}`
    }
    case 'move':
      if (p.action === 'moved') return `🏃 ${army(p.armyId)} marched ${p.steps} tile(s) to (${p.to})`
      if (p.action === 'blocked') return `🧱 ${army(p.armyId)} blocked by ${p.terrain} at (${p.at})`
      if (p.action === 'aborted') return `✖ ${army(p.armyId)} move aborted (${p.reason})`
      return `move: ${p.action}`
    case 'raid': {
      const bits = []
      if (p.foodGain ?? p.food) bits.push(`${round(p.foodGain ?? p.food)} food`)
      if (p.wealthGain ?? p.wealth) bits.push(`${round(p.wealthGain ?? p.wealth)} wealth`)
      if (p.popLost ?? p.popLoss) bits.push(`${round(p.popLost ?? p.popLoss)} pop`)
      const who = p.raiders != null ? ` (${p.raiders} raiders)` : ''
      return `🔥 ${army(p.armyId)} raided (${p.stance})${who}${bits.length ? ` — took ${bits.join(', ')}` : ''}`
    }
    case 'raid_skip':
      return `🔥 ${army(p.armyId)} has a raid order but raided nothing — ${p.reason}`
    case 'formation':
      if (p.action === 'raid-auto-assign') return `⚑ ${army(p.armyId)} committed ${round(p.men)} unassigned men to the Raiders bucket (raid order)`
      return `⚑ ${army(p.armyId)} formation: ${p.action}`
    case 'recruit':
      if (p.action === 'training-wage') return `🛡 Training wages ${round(p.cost)} gp`
      if (p.action === 'training-unpaid') return `⚠ ${faction(p.factionId)} can't pay ${round(p.owed)} gp training wages`
      if (p.action === 'ready') return `✅ ${round(p.men)} ${p.troop} finished training`
      if (p.action === 'delivered') return `📦 ${round(p.men)} men joined ${army(p.armyId)}${p.asReserves ? ' (reserves)' : ''}`
      return `recruit: ${p.action}`
    case 'caravan':
      if (p.action === 'delivered') return `🛒 Caravan delivered ${round(p.delivered)} gp supply to ${army(p.armyId)} (${p.surchargePct}% en-route loss)`
      if (p.action === 'moving') return `🛒 Caravan moving toward its army (${p.days}d out)`
      if (p.action === 'waiting') return `🛒 Caravan waiting (${p.reason})`
      if (p.action === 'lost') return `❌ Caravan lost — ${p.reason}`
      return `caravan: ${p.action}`
    case 'commander': {
      const who = p.name || `commander ${p.commanderId}`
      if (p.action === 'created') return `⚔ Commander ${who} raised${p.armyId ? ` with ${army(p.armyId)}` : ''}`
      if (p.action === 'seeded') return `⚔ Seeded commanders (${p.created} created, target ${p.perFaction}/faction)`
      if (p.action === 'assigned') return `⚔ ${who} took command of ${army(p.armyId)}`
      if (p.action === 'in-transit') return `🐎 ${who} riding to ${army(p.armyId)} — arrives day ${p.arriveDay}`
      if (p.action === 'arrived') return `⚔ ${who} arrived and took command of ${army(p.armyId)}`
      if (p.action === 'transfer-aborted') return `✖ ${who}'s transfer ended — the target army is gone; back at court`
      if (p.action === 'unassigned') return `⚔ ${who} left ${p.fromArmyId ? army(p.fromArmyId) : 'their post'} for court`
      if (p.action === 'status') return `⚔ ${who} is now ${p.status}`
      if (p.action === 'deleted') return `✖ Commander ${who} removed`
      if (p.action === 'abandoned-order') return `⚠ ${army(p.armyId)} — leaderless ${'>'}2 days — abandoned its ${p.abandoned} order and marches for ${p.dest && (p.dest.name || `(${p.dest.row},${p.dest.col})`)}`
      if (p.action === 'leaderless-halt') return `⚠ ${army(p.armyId)} — leaderless ${'>'}2 days — stood down its orders (already at a stronghold)`
      if (p.action === 'leaderless-stranded') return `⚠ ${army(p.armyId)} is leaderless with no reachable friendly stronghold — it halts`
      return `commander: ${p.action}`
    }
    case 'scouting_audit':
      return `🔎 ${formatScoutAudit(e, ctx)}`
    case 'dice_trace': {
      const n = (p.resolvers?.length || 0) + (p.scouting?.length || 0)
      return `🎲 ${n} engine dice rolled this day (${p.resolvers?.length || 0} resolver · ${p.scouting?.length || 0} scouting)`
    }
    default:
      return `${e.type}: ${JSON.stringify(p)}`
  }
}

// One scouting-audit row (why a spotter army did or didn't detect this day). `ctx.armyById`
// resolves ids to names. Mirrors the engine's audit kinds in scouting.js.
export function formatScoutAudit(e, ctx = {}) {
  const army = (id) => (ctx.armyById?.(id)?.name) || `army ${id}`
  const p = e.payload || e
  const who = army(p.army_id)
  const band = p.range ? `${p.range} ${p.rangeMi ? `(${p.rangeMi}mi)` : ''}`.trim() : ''
  switch (p.kind) {
    case 'skip':
      return p.scope === 'army'
        ? `${who} did not scout — ${p.reason}`
        : `${who} ${band}: no scouting — ${p.reason}`
    case 'scan':
      return `${who} ${band}: power ${p.power} / req ${p.req} → ${p.rolls} roll${p.rolls === 1 ? '' : 's'}${p.underPenalty ? ` (under-powered, +${p.underPenalty} penalty)` : ''}`
    case 'no-target':
      return `${who} ${band}: no undetected enemy in range`
    case 'roll': {
      const pens = [p.penUnder && `under +${p.penUnder}`, p.penTerrain && `terrain +${p.penTerrain}`, p.penFamiliar && `enemy-land +${p.penFamiliar}`].filter(Boolean).join(', ')
      const label = p.outcome === 'detect' ? '✦ DETECTED' : p.outcome === 'contact' ? '⚔ contact only' : '✗ missed'
      return `${who} ${band} vs ${army(p.target)}: d100 ${p.die}${pens ? ` (+${pens})` : ''} = ${p.roll} vs full ${p.full}/disc ${p.disc} → ${label}`
    }
    case 'close': {
      const label = p.outcome === 'detect' ? '✦ DETECTED' : p.outcome === 'contact' ? '⚔ contact' : '✗ missed'
      return `${who} co-located vs ${army(p.target)} (${p.terrain}): d100 ${p.die} ${p.penFamiliar ? `+${p.penFamiliar} ` : ''}−${p.closeBonus} = ${p.roll} vs full ${p.full}/disc ${p.disc} → ${label}`
    }
    default:
      return `${who}: ${JSON.stringify(p)}`
  }
}
